/**
 * StatsBar — four headline counters across the top of the Vault.
 *
 * Each counter is tabular-figure aligned so digits don't shift as
 * numbers update; the labels use muted ink to keep the numbers
 * visually dominant.
 */

import { useTranslation } from 'react-i18next';

import type { VaultStats } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber } from '@/utils/format';

export function StatsBar({ stats }: { stats: VaultStats }) {
  const { t } = useTranslation('vault');
  const locale = useLocaleStore((s) => s.locale);

  const items: Array<{ value: number; label: string }> = [
    { value: stats.total_gis, label: t('stats.total_gis') },
    { value: stats.total_signatures, label: t('stats.total_signatures') },
    { value: stats.total_verifications, label: t('stats.total_verifications') },
    { value: stats.total_forks, label: t('stats.total_forks') },
  ];

  return (
    <div
      role="group"
      aria-label={t('title')}
      className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4"
    >
      {items.map((it) => (
        <div
          key={it.label}
          className="flex flex-col gap-1 bg-surface px-5 py-4"
        >
          <span className="font-mono text-3xl font-semibold tabular-nums text-ink">
            {formatNumber(it.value, locale)}
          </span>
          <span className="text-xs uppercase tracking-wider text-ink-muted">
            {it.label}
          </span>
        </div>
      ))}
    </div>
  );
}
